import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import urls from "../../global/constants/UrlConstants";
import YhButton from "../../ui/YhButton";
import YhInput from "../../ui/YhInput";
import YhModal from "../../ui/YhModal";
import YhSelect from "../../ui/YhSelect";
import YhTable, { YhTableColumn } from "../../ui/YhTable";
import { YhDatePicker, YhDateTimePicker } from "../../ui/YhDateTimePicker";
import {
  formatDate,
  openErrorNotification,
  openSuccessNotification,
} from "../../helpers/methods";
import { getCustomerActivity, getCustomerById } from "./CustomerService";
import {
  getAllLeadHistory,
  getLeadById,
  getReminder,
  saveReminder,
} from "../AllLeads/components/ShowAll/ShowAllService";
import {
  getRemark,
  saveRemark,
} from "../MyLead/components/ShowAll/ShowAllService";
import {
  addTask,
  getTask,
} from "../AllLeads/components/stepper copy/Personal Details/PersonalDetailsService";
import { store } from "../../utils/store";
import { parseLeadExtraFields } from "./leadExtraFields";
import EntityCallHistory from "../Shared/EntityCallHistory";

type RouteParams = { id: string };
type TabKey = "activities" | "details" | "calls" | "task" | "reminder" | "notes";

const STORAGE_KEY = (id: string) => `customer-details-${id}`;

const TABS: { key: TabKey; label: string }[] = [
  { key: "activities", label: "Activities" },
  { key: "details", label: "Lead Details" },
  { key: "calls", label: "Calls" },
  { key: "task", label: "Task" },
  { key: "reminder", label: "Reminder" },
  { key: "notes", label: "Notes" },
];

const PRIORITY_OPTIONS = [
  { label: "Low", value: "Low" },
  { label: "Medium", value: "Medium" },
  { label: "High", value: "High" },
];

const EmptyState = ({
  title,
  subtitle,
  actionLabel,
  onAction,
}: {
  title: string;
  subtitle: string;
  actionLabel?: string;
  onAction?: () => void;
}) => (
  <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
    <div className="mb-5 flex h-28 w-28 items-center justify-center rounded-3xl bg-gradient-to-br from-[#F9F0F4] to-[#F5F3FF]">
      <svg
        width="56"
        height="56"
        viewBox="0 0 24 24"
        fill="none"
        className="text-[#841C39]"
      >
        <path
          d="M7 3h7l5 5v13a1 1 0 01-1 1H7a1 1 0 01-1-1V4a1 1 0 011-1z"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <path
          d="M14 3v5h5M8 13h8M8 17h5"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    </div>
    <p className="text-base font-semibold text-[#111827]">{title}</p>
    <p className="mt-1 max-w-sm text-sm text-[#6B7280]">{subtitle}</p>
    {actionLabel && onAction ? (
      <YhButton variant="primary" className="mt-5" onClick={onAction}>
        {actionLabel}
      </YhButton>
    ) : null}
  </div>
);

const DetailRow = ({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value?: React.ReactNode;
}) => (
  <div className="flex items-start gap-3 py-2.5">
    <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-[#F3F4F6] text-[#841C39]">
      {icon}
    </span>
    <div className="min-w-0">
      <p className="text-[11px] font-medium uppercase tracking-wide text-[#9CA3AF]">
        {label}
      </p>
      <p className="truncate text-sm font-medium text-[#111827]">
        {value || "—"}
      </p>
    </div>
  </div>
);

const formatPhone = (customer: any) => {
  if (!customer) return "";
  const code = customer.countryCode ? `+${customer.countryCode}` : "";
  const phone = customer.phoneNumber || customer.phone || "";
  return [code, phone].filter(Boolean).join(" ").trim();
};

const CustomerDetails = () => {
  const history = useHistory();
  const location = useLocation<any>();
  const { id } = useParams<RouteParams>();

  const [customer, setCustomer] = useState<any>(null);
  const [lead, setLead] = useState<any>(null);
  const [tab, setTab] = useState<TabKey>("activities");
  const [activities, setActivities] = useState<any[]>([]);
  const [reminders, setReminders] = useState<any[]>([]);
  const [notes, setNotes] = useState<any[]>([]);
  const [tasks, setTasks] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const [noteModal, setNoteModal] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [reminderModal, setReminderModal] = useState(false);
  const [reminderForm, setReminderForm] = useState({
    title: "",
    dateTime: "",
    notes: "",
  });
  const [taskModal, setTaskModal] = useState(false);
  const [savingTask, setSavingTask] = useState(false);
  const [taskForm, setTaskForm] = useState({
    priority: "",
    dueDate: "",
    closedDate: "",
    description: "",
  });

  const initialCustomer = useMemo(() => {
    if (location.state?.customer) return location.state.customer;
    try {
      const cached = sessionStorage.getItem(STORAGE_KEY(id));
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  }, [id, location.state]);

  const extraFields = useMemo(() => parseLeadExtraFields(lead), [lead]);

  useEffect(() => {
    if (initialCustomer?._id) {
      setCustomer(initialCustomer);
      sessionStorage.setItem(STORAGE_KEY(id), JSON.stringify(initialCustomer));
    }
    loadCustomer(id);
  }, [id, initialCustomer]);

  const normalizeActivityList = (res: any): any[] => {
    if (Array.isArray(res?.data)) return res.data;
    if (Array.isArray(res?.activities)) return res.activities;
    if (Array.isArray(res)) return res;
    return [];
  };

  const activityTimestamp = (item: any) => {
    const raw = item?.updatedAt || item?.createdAt || item?.date;
    const t = raw ? new Date(raw).getTime() : 0;
    return Number.isNaN(t) ? 0 : t;
  };

  const loadLeadTabData = async (leadId: string) => {
    if (!leadId) {
      setReminders([]);
      setNotes([]);
      setTasks([]);
      return;
    }
    const [rem, remark, taskRes] = await Promise.all([
      getReminder(leadId).catch(() => ({ data: [] })),
      getRemark(leadId).catch(() => ({ data: [] })),
      getTask(leadId).catch(() => ({ data: [] })),
    ]);
    setReminders(Array.isArray(rem?.data) ? rem.data : []);
    setNotes(Array.isArray(remark?.data) ? remark.data : []);
    setTasks(Array.isArray(taskRes?.data) ? taskRes.data : []);
  };

  const loadLinkedLead = async (leadId: string) => {
    if (!leadId) {
      setLead(null);
      await loadLeadTabData("");
      return;
    }
    try {
      const res = await getLeadById(leadId);
      const leadData = Array.isArray(res?.data)
        ? res.data[0]
        : res?.data || res;
      setLead(leadData?._id ? leadData : null);
    } catch {
      setLead(null);
    }
    await loadLeadTabData(leadId);
  };

  const loadCustomer = async (customerId: string) => {
    if (!customerId) return;
    setLoading(true);
    try {
      const detailRes = await getCustomerById(customerId);
      const data = detailRes?.data || detailRes;
      if (data?._id) {
        setCustomer(data);
        sessionStorage.setItem(STORAGE_KEY(customerId), JSON.stringify(data));
      }

      const leadId = data?.leadId || initialCustomer?.leadId || "";
      const [customerActivityRes, leadHistoryRes] = await Promise.all([
        getCustomerActivity(customerId).catch(() => ({ data: [] })),
        leadId
          ? getAllLeadHistory(leadId).catch(() => ({ data: [] }))
          : Promise.resolve({ data: [] }),
        loadLinkedLead(leadId),
      ]);

      const customerActs = normalizeActivityList(customerActivityRes).map(
        (item: any) => ({
          ...item,
          __source: "customer",
        })
      );
      const leadActs = normalizeActivityList(leadHistoryRes).map(
        (item: any) => ({
          ...item,
          __source: "lead",
        })
      );

      const merged = [...leadActs, ...customerActs].sort(
        (a, b) => activityTimestamp(b) - activityTimestamp(a)
      );
      setActivities(merged);
    } catch (error: any) {
      if (!initialCustomer?._id) {
        openErrorNotification(error?.message || "Failed to load customer");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    history.push(urls.customerListViewPath);
  };

  const leadId = customer?.leadId || lead?._id || "";

  const handleSaveNote = async () => {
    if (!noteText.trim() || !leadId) return;
    try {
      const payload = {
        leadId,
        notes: noteText.trim(),
        date: new Date().toISOString().split("T")[0],
        time: new Date().toLocaleTimeString("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
        }),
        title: "",
      };
      const res = await saveRemark(payload);
      if (res?.success === 1 || res?.data) {
        openSuccessNotification(res?.message || "Note added");
        setNoteModal(false);
        setNoteText("");
        const remark = await getRemark(leadId);
        setNotes(Array.isArray(remark?.data) ? remark.data : []);
      }
    } catch (e: any) {
      openErrorNotification(e?.message || "Failed to save note");
    }
  };

  const handleSaveReminder = async () => {
    if (!reminderForm.title || !reminderForm.dateTime || !leadId) return;
    try {
      const [date, time] = reminderForm.dateTime.split("T");
      const body = {
        leadId,
        title: reminderForm.title,
        notes: reminderForm.notes,
        date,
        time,
      };
      const res = await saveReminder(body);
      if (res?.success === 1 || res?.data) {
        openSuccessNotification(res?.message || "Reminder created");
        setReminderModal(false);
        setReminderForm({ title: "", dateTime: "", notes: "" });
        const rem = await getReminder(leadId);
        setReminders(Array.isArray(rem?.data) ? rem.data : []);
      }
    } catch (e: any) {
      openErrorNotification(e?.message || "Failed to save reminder");
    }
  };

  const handleSaveTask = async () => {
    if (!leadId) return;
    if (!taskForm.priority) {
      openErrorNotification("Priority is required");
      return;
    }
    setSavingTask(true);
    try {
      const payload = {
        leadId,
        userId: JSON.stringify([store.getState().auth.userId]),
        priority: taskForm.priority,
        assignTo: JSON.stringify([]),
        assignToMember: JSON.stringify([]),
        dueDate: taskForm.dueDate,
        closedDate: taskForm.closedDate,
        description: taskForm.description,
      };
      const res = await addTask(payload);
      if (res?.success === 1 || res?.data) {
        openSuccessNotification(res?.message || "Task created successfully");
        setTaskModal(false);
        setTaskForm({
          priority: "",
          dueDate: "",
          closedDate: "",
          description: "",
        });
        const taskRes = await getTask(leadId);
        setTasks(Array.isArray(taskRes?.data) ? taskRes.data : []);
      } else {
        openErrorNotification(res?.message || "Failed to create task");
      }
    } catch (e: any) {
      openErrorNotification(e?.message || "Failed to create task");
    } finally {
      setSavingTask(false);
    }
  };

  const initials = String(customer?.name || lead?.leadName || "C")
    .trim()
    .charAt(0)
    .toUpperCase();

  const displayName = customer?.name || lead?.leadName;
  const displayEmail = customer?.email || lead?.leadEmail;
  const displayPhone =
    formatPhone(customer) || lead?.leadPhone || "";

  const notesRows = notes.map((n, i) => ({ ...n, __index: i, id: n._id || i }));
  const reminderRows = reminders.map((n, i) => ({
    ...n,
    __index: i,
    id: n._id || i,
  }));
  const taskRows = tasks.map((n, i) => ({
    ...n,
    __index: i,
    id: n.task_id || n._id || i,
  }));

  const noteColumns: YhTableColumn[] = [
    {
      field: "sno",
      headerName: "S.No",
      minWidth: 70,
      renderCell: ({ row }) => row.__index + 1,
    },
    { field: "notes", headerName: "Notes", flex: 1, minWidth: 220 },
    {
      field: "updatedBy",
      headerName: "Updated By",
      minWidth: 140,
      renderCell: ({ row }) => row.updatedBy || row.user_name || "—",
    },
    {
      field: "date",
      headerName: "Date Created",
      minWidth: 140,
      renderCell: ({ row }) =>
        [row.date, row.time].filter(Boolean).join(" ") || "—",
    },
  ];

  const reminderColumns: YhTableColumn[] = [
    {
      field: "sno",
      headerName: "S.No",
      minWidth: 70,
      renderCell: ({ row }) => row.__index + 1,
    },
    { field: "title", headerName: "Title", flex: 1, minWidth: 160 },
    {
      field: "notes",
      headerName: "Notes",
      minWidth: 180,
      renderCell: ({ row }) => row.notes || "—",
    },
    {
      field: "date",
      headerName: "Date / Time",
      minWidth: 140,
      renderCell: ({ row }) =>
        [row.date, row.time].filter(Boolean).join(" ") || "—",
    },
  ];

  const taskColumns: YhTableColumn[] = [
    {
      field: "sno",
      headerName: "S.No",
      minWidth: 70,
      renderCell: ({ row }) => row.__index + 1,
    },
    {
      field: "description",
      headerName: "Task",
      flex: 1,
      minWidth: 180,
      renderCell: ({ row }) =>
        row.description || row.title || row.task || "—",
    },
    {
      field: "priority",
      headerName: "Priority",
      minWidth: 110,
      renderCell: ({ row }) => row.priority || row.status || "—",
    },
    {
      field: "dueDate",
      headerName: "Due Date",
      minWidth: 130,
      renderCell: ({ row }) => row.dueDate || row.date || "—",
    },
    {
      field: "closedDate",
      headerName: "Close Date",
      minWidth: 130,
      renderCell: ({ row }) =>
        row.closedDate || row.closeDate || "—",
    },
    {
      field: "assignedFrom",
      headerName: "Assigned From",
      minWidth: 140,
      renderCell: ({ row }) =>
        row.assignedFrom || row.user_name || "—",
    },
  ];

  if (!customer?._id && loading) {
    return (
      <div className="flex h-[calc(100vh-68px)] items-center justify-center bg-white">
        <p className="text-sm text-[#6B7280]">Loading customer...</p>
      </div>
    );
  }

  if (!customer?._id) {
    return (
      <div className="flex h-[calc(100vh-68px)] items-center justify-center bg-white">
        <div className="text-center">
          <p className="mb-4 text-sm text-[#6B7280]">Customer not found.</p>
          <YhButton variant="primary" onClick={handleBack}>
            Back to Customer List
          </YhButton>
        </div>
      </div>
    );
  }

  const noLeadHint = !leadId
    ? "This customer is not linked to a lead."
    : undefined;

  return (
    <div className="relative flex h-[calc(100vh-68px)] max-h-[calc(100vh-68px)] flex-col overflow-hidden bg-white p-4 pb-0 sm:p-5 sm:pb-0 lg:p-6 lg:pb-0">
      <div className="flex min-h-0 flex-1 gap-4 overflow-hidden pb-6">
        <aside className="flex w-full max-w-[320px] shrink-0 flex-col overflow-hidden rounded-2xl border border-[#EEF0F5] bg-white shadow-[0_4px_20px_rgba(15,23,42,0.04)] lg:max-w-[340px]">
          <div className="bg-gradient-to-br from-[#841C39] via-[#A02448] to-[#A02448] px-5 pb-5 pt-6 text-white">
            <div className="flex items-center gap-3">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-full bg-white/20 text-lg font-bold backdrop-blur">
                {customer.profileImage ? (
                  <img
                    src={customer.profileImage}
                    alt={displayName || "Customer"}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  initials
                )}
              </div>
              <div className="min-w-0">
                <p className="truncate text-lg font-semibold">
                  {displayName || "—"}
                </p>
                <p className="mt-0.5 inline-flex rounded-md bg-white/15 px-2 py-0.5 text-xs">
                  {customer._id}
                </p>
              </div>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-auto px-4 py-4">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-[#6B7280]">
              Contact Information
            </p>
            <DetailRow
              label="Full Name"
              value={displayName}
              icon={<span className="text-xs font-bold">N</span>}
            />
            <DetailRow
              label="Email"
              value={displayEmail}
              icon={<span className="text-xs font-bold">@</span>}
            />
            <DetailRow
              label="Phone Number"
              value={displayPhone}
              icon={<span className="text-xs font-bold">#</span>}
            />
            <DetailRow
              label="Alt. Number"
              value={customer?.alternativeNumber}
              icon={<span className="text-xs font-bold">A</span>}
            />

            <div className="my-3 border-t border-[#EEF0F5]" />
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-[#6B7280]">
              Customer Details
            </p>
            <DetailRow
              label="Source"
              value={customer?.source}
              icon={<span className="text-xs font-bold">S</span>}
            />
            <DetailRow
              label="Project Name"
              value={customer?.projectName || customer?.project_name}
              icon={<span className="text-xs font-bold">P</span>}
            />
            <DetailRow
              label="Gender"
              value={customer?.gender}
              icon={<span className="text-xs font-bold">G</span>}
            />
            <DetailRow
              label="City"
              value={customer?.city}
              icon={<span className="text-xs font-bold">C</span>}
            />
            <DetailRow
              label="State"
              value={customer?.state}
              icon={<span className="text-xs font-bold">ST</span>}
            />
            <DetailRow
              label="Country"
              value={customer?.country}
              icon={<span className="text-xs font-bold">CO</span>}
            />
            <DetailRow
              label="Address"
              value={customer?.address}
              icon={<span className="text-xs font-bold">AD</span>}
            />
            <DetailRow
              label="Location"
              value={customer?.location}
              icon={<span className="text-xs font-bold">L</span>}
            />
            <DetailRow
              label="Created At"
              value={
                customer?.createdAt ? formatDate(customer.createdAt) : undefined
              }
              icon={<span className="text-xs font-bold">D</span>}
            />
            <DetailRow
              label="Lead ID"
              value={customer?.leadId}
              icon={<span className="text-xs font-bold">ID</span>}
            />
          </div>
        </aside>

        <section className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-2xl border border-[#EEF0F5] bg-white shadow-[0_4px_20px_rgba(15,23,42,0.04)]">
          <div className="flex shrink-0 gap-1 overflow-x-auto border-b border-[#EEF0F5] px-3 pt-2">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={`relative whitespace-nowrap px-4 py-3 text-sm font-medium transition ${
                  tab === t.key
                    ? "text-[#841C39]"
                    : "text-[#6B7280] hover:text-[#111827]"
                }`}
              >
                {t.label}
                {tab === t.key ? (
                  <span className="absolute inset-x-2 bottom-0 h-0.5 rounded-full bg-[#841C39]" />
                ) : null}
              </button>
            ))}
          </div>

          <div className="min-h-0 flex-1 overflow-auto p-4 sm:p-5">
            {tab === "activities" && (
              <div>
                <div className="mb-4">
                  <h2 className="text-lg font-semibold text-[#111827]">
                    Activities
                  </h2>
                  <p className="text-sm text-[#6B7280]">
                    {leadId
                      ? "Full timeline including lead history after conversion to customer."
                      : "Timeline of updates for this customer."}
                  </p>
                </div>
                {activities.length === 0 ? (
                  <EmptyState
                    title="No activities found"
                    subtitle="There is no activity history for this customer yet."
                  />
                ) : (
                  <div className="space-y-0 border-l-2 border-[#F9F0F4] pl-5">
                    {activities.map((item: any, index: number) => {
                      const statusLabel =
                        item.new_value ||
                        item.activityType ||
                        item.title ||
                        item.action ||
                        item.stage ||
                        "Update";
                      const userName =
                        item.users?.[0]?.name ||
                        item.user_name ||
                        item.updatedByName ||
                        "";
                      const previousStage = String(item.old_value || "").trim();
                      const fromLabel =
                        previousStage &&
                        previousStage !== String(statusLabel)
                          ? `Changed from ${previousStage}`
                          : "";
                      const rawDate =
                        item.updatedAt || item.createdAt || item.date;
                      let dateTimeLabel = "";
                      if (rawDate) {
                        const d = new Date(rawDate);
                        dateTimeLabel = Number.isNaN(d.getTime())
                          ? String(rawDate)
                          : d.toLocaleString("en-GB", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                              second: "2-digit",
                              hour12: false,
                            });
                      }

                      return (
                        <div
                          key={`${item.__source || "act"}-${item.log_id || item._id || item.id || index}`}
                          className="relative pb-6"
                        >
                          <span className="absolute -left-[27px] top-1.5 h-3 w-3 rounded-full border-2 border-[#841C39] bg-white" />
                          <p className="text-sm font-semibold text-[#111827]">
                            {statusLabel}
                          </p>
                          {userName ? (
                            <p className="mt-0.5 text-xs text-[#6B7280]">
                              Lead status changed by {userName}
                            </p>
                          ) : null}
                          {fromLabel ? (
                            <p className="mt-0.5 text-xs text-[#9CA3AF]">
                              {fromLabel}
                            </p>
                          ) : null}
                          {dateTimeLabel ? (
                            <p className="mt-0.5 text-xs text-[#6B7280]">
                              {dateTimeLabel}
                            </p>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {tab === "details" && (
              <div>
                <div className="mb-4">
                  <h2 className="text-lg font-semibold text-[#111827]">
                    Lead Details
                  </h2>
                  <p className="text-sm text-[#6B7280]">
                    Linked lead form information for this customer.
                  </p>
                </div>

                {leadId ? (
                  <div className="rounded-2xl border border-[#EEF0F5] bg-[#F8F9FC] p-4 sm:p-5">
                    <p className="mb-3 text-sm font-semibold text-[#841C39]">
                      Lead Form Details
                    </p>
                    <div className="grid gap-4 sm:grid-cols-2">
                      {(
                        [
                          ["Form Name", lead?.form_name],
                          ["Developer Name", lead?.developer_name],
                          ["Project Name", lead?.project_name],
                          ["Project Type", lead?.projecttype_name],
                          ["Stage", lead?.stage],
                          ["Source", lead?.source],
                          ["Full Name", lead?.leadName],
                          ["Email", lead?.leadEmail],
                          ["Phone", lead?.leadPhone],
                        ] as [string, string | undefined][]
                      ).map(([label, value]) => (
                        <div key={label}>
                          <p className="mb-1 text-xs font-medium text-[#6B7280]">
                            {label}
                          </p>
                          <div className="rounded-xl border border-[#E5E7EB] bg-white px-3 py-2.5 text-sm text-[#111827]">
                            {value || "—"}
                          </div>
                        </div>
                      ))}
                    </div>

                    {extraFields.length > 0 ? (
                      <>
                        <p className="mb-3 mt-5 text-sm font-semibold text-[#841C39]">
                          Additional Fields
                        </p>
                        <div className="grid gap-4 sm:grid-cols-2">
                          {extraFields.map((field) => (
                            <div key={`extra-${field.label}-${field.value}`}>
                              <p className="mb-1 text-xs font-medium text-[#6B7280]">
                                {field.label}
                              </p>
                              <div className="rounded-xl border border-[#E5E7EB] bg-white px-3 py-2.5 text-sm text-[#111827]">
                                {field.value || "—"}
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    ) : null}
                  </div>
                ) : (
                  <EmptyState
                    title="No linked lead"
                    subtitle="This customer is not linked to a lead form."
                  />
                )}
              </div>
            )}

            {tab === "calls" && (
              <EntityCallHistory
                phone={displayPhone || lead?.leadPhoneFull}
                title="Call history"
                subtitle="All IVR and click-to-call attempts for this customer."
              />
            )}

            {tab === "task" && (
              <div className="flex h-full min-h-[360px] flex-col">
                <div className="mb-4 flex items-start justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-semibold text-[#111827]">
                      Task
                    </h2>
                    <p className="text-sm text-[#6B7280]">
                      {noLeadHint ||
                        "Same tasks as the linked lead after conversion."}
                    </p>
                  </div>
                  <YhButton
                    variant="secondary"
                    size="sm"
                    disabled={!leadId}
                    onClick={() => setTaskModal(true)}
                  >
                    + Add Task
                  </YhButton>
                </div>
                {taskRows.length === 0 ? (
                  <EmptyState
                    title="No tasks found"
                    subtitle={
                      noLeadHint ||
                      "There are no tasks added for this lead yet."
                    }
                    actionLabel={leadId ? "+ Add Your First Task" : undefined}
                    onAction={leadId ? () => setTaskModal(true) : undefined}
                  />
                ) : (
                  <YhTable
                    columns={taskColumns}
                    rows={taskRows}
                    getRowId={(r) => r.id || r._id}
                    rowHeight={56}
                    showPagination={false}
                    maxBodyHeight="calc(100vh - 320px)"
                  />
                )}
              </div>
            )}

            {tab === "reminder" && (
              <div className="flex h-full min-h-[360px] flex-col">
                <div className="mb-4 flex items-start justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-semibold text-[#111827]">
                      Reminder
                    </h2>
                    <p className="text-sm text-[#6B7280]">
                      {noLeadHint ||
                        "Same reminders as the linked lead after conversion."}
                    </p>
                  </div>
                  <YhButton
                    variant="secondary"
                    size="sm"
                    disabled={!leadId}
                    onClick={() => setReminderModal(true)}
                  >
                    + Add Reminder
                  </YhButton>
                </div>
                {reminderRows.length === 0 ? (
                  <EmptyState
                    title="No reminders found"
                    subtitle={
                      noLeadHint ||
                      "There are no reminders added for this lead yet."
                    }
                    actionLabel={
                      leadId ? "+ Add Your First Reminder" : undefined
                    }
                    onAction={
                      leadId ? () => setReminderModal(true) : undefined
                    }
                  />
                ) : (
                  <YhTable
                    columns={reminderColumns}
                    rows={reminderRows}
                    getRowId={(r) => r.id || r._id}
                    rowHeight={56}
                    showPagination={false}
                    maxBodyHeight="calc(100vh - 320px)"
                    loading={loading}
                  />
                )}
              </div>
            )}

            {tab === "notes" && (
              <div className="flex h-full min-h-[360px] flex-col">
                <div className="mb-4 flex items-start justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-semibold text-[#111827]">
                      Notes
                    </h2>
                    <p className="text-sm text-[#6B7280]">
                      {noLeadHint ||
                        "Same notes as the linked lead after conversion."}
                    </p>
                  </div>
                  <YhButton
                    variant="secondary"
                    size="sm"
                    disabled={!leadId}
                    onClick={() => setNoteModal(true)}
                  >
                    + Add Note
                  </YhButton>
                </div>
                {notesRows.length === 0 ? (
                  <EmptyState
                    title="No notes found"
                    subtitle={
                      noLeadHint ||
                      "There are no notes added for this lead yet."
                    }
                    actionLabel={leadId ? "+ Add Your First Note" : undefined}
                    onAction={leadId ? () => setNoteModal(true) : undefined}
                  />
                ) : (
                  <YhTable
                    columns={noteColumns}
                    rows={notesRows}
                    getRowId={(r) => r.id || r._id}
                    rowHeight={56}
                    showPagination={false}
                    maxBodyHeight="calc(100vh - 320px)"
                    loading={loading}
                  />
                )}
              </div>
            )}
          </div>
        </section>
      </div>

      <YhModal
        open={noteModal}
        onClose={() => setNoteModal(false)}
        title="Create Note"
        footer={
          <div className="flex justify-end gap-2">
            <YhButton variant="secondary" onClick={() => setNoteModal(false)}>
              Cancel
            </YhButton>
            <YhButton variant="primary" onClick={handleSaveNote}>
              Save Note
            </YhButton>
          </div>
        }
      >
        <p className="mb-2 text-sm text-[#6B7280]">
          Notes are saved against the linked lead.
        </p>
        <textarea
          className="min-h-[120px] w-full rounded-xl border border-[#E5E7EB] px-3 py-2 text-sm outline-none focus:border-[#A02448]"
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          placeholder="Write a note..."
        />
      </YhModal>

      <YhModal
        open={reminderModal}
        onClose={() => setReminderModal(false)}
        title="Create Reminder"
        footer={
          <div className="flex justify-end gap-2">
            <YhButton
              variant="secondary"
              onClick={() => setReminderModal(false)}
            >
              Cancel
            </YhButton>
            <YhButton variant="primary" onClick={handleSaveReminder}>
              Save Reminder
            </YhButton>
          </div>
        }
      >
        <div className="space-y-3">
          <YhInput
            label="Reminder Title"
            value={reminderForm.title}
            onChange={(e) =>
              setReminderForm((p) => ({ ...p, title: e.target.value }))
            }
          />
          <YhDateTimePicker
            label="Date and Time"
            value={reminderForm.dateTime}
            onChange={(val) =>
              setReminderForm((p) => ({ ...p, dateTime: val }))
            }
          />
          <div>
            <p className="mb-1 text-xs font-medium text-[#6B7280]">Notes</p>
            <textarea
              className="min-h-[80px] w-full rounded-xl border border-[#E5E7EB] px-3 py-2 text-sm outline-none focus:border-[#A02448]"
              value={reminderForm.notes}
              onChange={(e) =>
                setReminderForm((p) => ({ ...p, notes: e.target.value }))
              }
            />
          </div>
        </div>
      </YhModal>

      <YhModal
        open={taskModal}
        onClose={() => setTaskModal(false)}
        title="Create Task"
        footer={
          <div className="flex justify-end gap-2">
            <YhButton variant="secondary" onClick={() => setTaskModal(false)}>
              Cancel
            </YhButton>
            <YhButton
              variant="primary"
              disabled={savingTask}
              onClick={handleSaveTask}
            >
              {savingTask ? "Saving..." : "Save Task"}
            </YhButton>
          </div>
        }
      >
        <div className="space-y-3">
          <YhSelect
            label="Priority"
            placeholder="Select priority"
            searchable={false}
            showOptionIcon={false}
            value={taskForm.priority}
            options={PRIORITY_OPTIONS}
            onChange={(e) =>
              setTaskForm((p) => ({ ...p, priority: e.target.value || "" }))
            }
          />
          <YhDatePicker
            label="Task Due Date"
            value={taskForm.dueDate}
            onChange={(val) =>
              setTaskForm((p) => ({ ...p, dueDate: val }))
            }
          />
          <YhDatePicker
            label="Task Close Date"
            value={taskForm.closedDate}
            onChange={(val) =>
              setTaskForm((p) => ({ ...p, closedDate: val }))
            }
          />
          <div>
            <p className="mb-1 text-xs font-medium text-[#6B7280]">
              Description
            </p>
            <textarea
              className="min-h-[80px] w-full rounded-xl border border-[#E5E7EB] px-3 py-2 text-sm outline-none focus:border-[#A02448]"
              value={taskForm.description}
              onChange={(e) =>
                setTaskForm((p) => ({ ...p, description: e.target.value }))
              }
            />
          </div>
        </div>
      </YhModal>
    </div>
  );
};

export default CustomerDetails;
